/**
 * Check if parameter `x` is defined
 * @param x a nullable value
 * @returns `false` if it's null or undefined, `true` otherwise
 */
export function isDefined<T>(x: T | null | undefined): x is T {
  return x != null;
}


/**
 * Check if parameter `x` is not defined
 * @param x a nullable value
 * @returns `true` if it's null or undefined, `false` otherwise
 */
export function isNotDefined<T>(x: T | null | undefined): x is null | undefined {
  return x == null;
}


/**
 * Check if parameter `x` has a length equal to 0
 * @param x a nullable value with a length: number property
 * @returns `true` if it's empty, `false` otherwise
 */
export function isEmpty(x: string | null | undefined): x is '' | null | undefined;
export function isEmpty(x: ArrayLike<unknown> | null | undefined): x is [] | null | undefined;
export function isEmpty<T extends { length: number }>(x: T | null | undefined): boolean {
  return x == null || x.length === 0;
}


/**
 * Check if parameter `x` has a length greater than 0
 * @param x a nullable value with a length: number property
 * @returns `false` if it's empty, `true` otherwise
 */
export function isNonEmpty<T extends string>(x: T | null | undefined): x is Exclude<T, ''>;
export function isNonEmpty<T>(x: ArrayLike<T> | null | undefined): x is readonly [T, ...T[]];
export function isNonEmpty<T extends { length: number }>(x: T | null | undefined): x is T {
  return x != null && x.length > 0;
}


export function identity<T>(x: T): T {
  return x;
}


export const isStringNonEmpty = (value: string | null | undefined) =>
  isDefined(value) && value.length > 0;


/**
 * Compare equality of two Arrays
 * @param arrayA first array
 * @param arrayB second array
 * @returns true if arrays have the exact same elements, false otherwise
 */
export function areArraysEqual<T>(arrayA: T[], arrayB: T[]): boolean {
  if (arrayA.length !== arrayB.length) {
    return false;
  }
  const setA = new Set(arrayA);
  const setB = new Set(arrayB);
  return Array.from(setA).every(value => setB.has(value));
}
